import { portalLocale } from "../../i18n/portal.js";
/**
 * Days, periods and dates for the Payouts page — plan-005 OP-38.
 *
 * Every payout cut-off, every statement period and every CSV is in **Europe/Athens** (design
 * §18.8.4), whatever the browser's own zone is: a shop owner in Athens reading on a laptop set to
 * UTC must see the same "This month" the API cuts. So a day here is always the Athens calendar
 * day, computed with `Intl` and never with the machine's local date.
 */
import type { Money } from "@wetalk/domain";

import { formatMoney } from "../../money.js";

export const PAYOUT_TIME_ZONE = "Europe/Athens";

const ATHENS_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: PAYOUT_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const ATHENS_DATE = () => new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), {
  timeZone: PAYOUT_TIME_ZONE,
  day: "numeric",
  month: "short",
  year: "numeric",
});

const ATHENS_DATE_TIME = () => new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), {
  timeZone: PAYOUT_TIME_ZONE,
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** `YYYY-MM-DD` of an instant, in Athens. */
export function athensDayOf(at: Date): string {
  return ATHENS_DAY.format(at);
}

/** "26 Sept 2026" — an instant's Athens date. */
export function athensDateText(at: Date): string {
  return ATHENS_DATE().format(at);
}

/** "28 Sept 2026, 00:00" — an instant's Athens date and time. */
export function athensDateTimeText(at: Date): string {
  return ATHENS_DATE_TIME().format(at);
}

/** "26 Sept 2026" for a `YYYY-MM-DD` day (read as a calendar day, not an instant). */
export function dayText(day: string): string {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" }).format(
    new Date(Date.UTC(year, month - 1, date)),
  );
}

/** The day `count` days after `day` (negative for before), as `YYYY-MM-DD`. */
export function dayPlus(day: string, count: number): string {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, date + count)).toISOString().slice(0, 10);
}

/** The statement periods the page offers, in its order. Each is at most 366 days (the API's limit). */
export const PAYOUT_PERIOD = ["This month", "Last month", "Last 90 days"] as const;
export type PayoutPeriod = (typeof PAYOUT_PERIOD)[number];

/** A period as the API takes it: whole Athens days, `to` inclusive. */
export function payoutPeriodOf(period: PayoutPeriod, now: Date): { readonly from: string; readonly to: string } {
  const today = athensDayOf(now);
  const firstOfMonth = `${today.slice(0, 8)}01`;
  switch (period) {
    case "This month":
      return { from: firstOfMonth, to: today };
    case "Last month": {
      const lastOfPrevious = dayPlus(firstOfMonth, -1);
      return { from: `${lastOfPrevious.slice(0, 8)}01`, to: lastOfPrevious };
    }
    case "Last 90 days":
      return { from: dayPlus(today, -89), to: today };
  }
}

/** `+€24.00` / `−€18.50` / `€0.00` — a signed ledger amount. */
export function signedMoneyText(amount: Money): string {
  const whole = formatMoney({ amountMinor: Math.abs(amount.amountMinor), currency: amount.currency });
  if (amount.amountMinor > 0) return `+${whole}`;
  if (amount.amountMinor < 0) return `−${whole}`;
  return whole;
}

/** The file the CSV is saved as: the book and the period, as the API names its attachment. */
export function payoutCsvFileName(providerMode: string, from: string, to: string): string {
  return `wetalk-payouts-${providerMode}-${from}-to-${to}.csv`;
}

/** Saves text as a file in the browser — the API's CSV, built when asked and never stored. */
export function saveCsv(text: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
